/**
 * Count consecutive wall-dwell escapes that left paint on the same frame.
 * Multi-frame only — used to force media remount without loopWrapScheduleResync (#1551 / PLAYER-1J).
 */

/** Remount after this many same-paint escapes (~2× slot+500ms). */
export const WALL_DWELL_ESCAPE_SAME_PAINT_REMOUNT_AFTER = 2

export interface IWallDwellEscapeSamePaintState {
  consecutiveSamePaintEscapes: number
}

export interface INoteWallDwellEscapeSamePaintInput {
  consecutiveSamePaintEscapes: number
  /** Painted frame id immediately before the escape presentation commit. */
  paintedFrameIdBefore: string | null
  /** Painted frame id after commit (or nextTick); null if surface empty. */
  paintedFrameIdAfter: string | null
}

export interface INoteWallDwellEscapeSamePaintResult {
  consecutiveSamePaintEscapes: number
  shouldRemount: boolean
}

/**
 * Sticky / Phase-C crossfade holds keep outgoing pixels on purpose. Crediting same-paint escapes
 * during that window remounts blob/HLS mid-transition (`clearStickyOutgoingHold('supersede')`) and
 * feels like sluggish video on low-tier — especially with `playerCreativeCrossfadeOnLowCapacity`.
 */
export function shouldCreditWallDwellEscapeSamePaint(input: {
  stickyOutgoingHoldActive: boolean
}): boolean {
  return !input.stickyOutgoingHoldActive
}

/**
 * Rising-edge counter for wall-dwell escapes where paint identity did not change.
 * Resets when paint advances or either id is missing.
 */
export function noteWallDwellEscapeSamePaint(
  input: INoteWallDwellEscapeSamePaintInput
): INoteWallDwellEscapeSamePaintResult {
  const { paintedFrameIdBefore, paintedFrameIdAfter } = input
  if (
    paintedFrameIdBefore == null
    || paintedFrameIdBefore === ''
    || paintedFrameIdAfter == null
    || paintedFrameIdAfter === ''
    || paintedFrameIdAfter !== paintedFrameIdBefore
  ) {
    return { consecutiveSamePaintEscapes: 0, shouldRemount: false }
  }
  const consecutiveSamePaintEscapes = input.consecutiveSamePaintEscapes + 1
  return {
    consecutiveSamePaintEscapes,
    shouldRemount: consecutiveSamePaintEscapes >= WALL_DWELL_ESCAPE_SAME_PAINT_REMOUNT_AFTER
  }
}

/** Clear counter when paint advances without an escape (normal playback). */
export function resetWallDwellEscapeSamePaintCounter(): IWallDwellEscapeSamePaintState {
  return { consecutiveSamePaintEscapes: 0 }
}
